import React, { useEffect, lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import { motion } from 'framer-motion';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { api } from './api';

// Fallback loader for lazy loading
import Loader from './pages/Loader';
import VisitCounter from './pages/Visit';

// Lazy-loaded pages/components
const Footer = lazy(() => import('./components/Footer'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const HomePage = lazy(() => import('./components/HomePage'));
const Layout = lazy(() => import('./components/Layout'));
const AboutSection = lazy(() => import('./pages/AboutSection'));
const ServicesSection = lazy(() => import('./pages/ServicesSection'));
const TestPreparation = lazy(() => import('./pages/TestPrepration'));
const Contact = lazy(() => import('./pages/Contact'));
const GetQuote = lazy(() => import('./pages/GetQuote'));
const GetConsultant = lazy(() => import('./pages/GetConsultant'));
const ConsultationSection = lazy(() => import('./pages/Faq'));
const Team = lazy(() => import('./pages/Team'));
const Login = lazy(() => import('./pages/Login'));
const AllConsultants = lazy(() => import('./pages/AllConsultants'));
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));

function WebsiteTracker() {
  const location = useLocation();

  useEffect(() => {
    const lastVisit = localStorage.getItem("lastVisitTime");
    const now = Date.now();

    if (!lastVisit || now - Number(lastVisit) > 2 * 60 * 60 * 1000) {
      localStorage.setItem("lastVisitTime", String(now));
      api.get("/api/public-visit").catch((error) => {
        console.error("Visit tracking error:", error.message);
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (event) => {
      const clickedElement =
        event.target instanceof Element ? event.target : event.target?.parentElement;
      const target = clickedElement?.closest("a, button, input, textarea, select, [role='button']");
      const element = target?.tagName?.toLowerCase() || clickedElement?.tagName?.toLowerCase() || "unknown";
      const label =
        target?.innerText?.trim() ||
        target?.getAttribute?.("aria-label") ||
        target?.getAttribute?.("placeholder") ||
        target?.getAttribute?.("href") ||
        "Website click";

      api
        .post("/api/track-click", {
          path: window.location.pathname,
          label,
          element,
        })
        .catch(() => {});
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);

  return null;
}

function AppWrapper() {
  const location = useLocation();

  useEffect(() => {
    AOS.init({
      duration: 600,
      once: true,
      offset: 80,
      disable: () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    });
  }, []);

  return (
    <motion.div
      key={location.pathname}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white min-h-screen font-[Poppins]"
    >
      <WebsiteTracker />
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route element={<Layout />}>
            <Route path="/about" element={<AboutSection />} />
            <Route path="/services" element={<ServicesSection />} />
            <Route path="/visit-stats" element={<VisitCounter />} />
            <Route path="/test-preparation" element={<TestPreparation />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/quote" element={<GetQuote />} />
            <Route path="/consultant" element={<GetConsultant />} />
            <Route path="/faq" element={<ConsultationSection />} />
            <Route path="/team" element={<Team />} />

            {/* Protected Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/consultants"
              element={
                <ProtectedRoute>
                  <AllConsultants />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Admin Login (No Layout) */}
          <Route path="/admin/login" element={<Login />} />
        </Routes>

      </Suspense>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </motion.div>
  );
}

function App() {
  return (
    <Router>
      <AppWrapper />
    </Router>
  );
}

export default App;
